import { createElement, type ReactNode } from "react";

import type { PageTemplate } from "../contract.js";

/**
 * Renders one page of a template: each section with its content, then the layout. Every section
 * is marked (`data-template-section`), so a parity suite finds it whatever the layout; an optional
 * section left out of the content keeps its empty marker and renders nothing. A section the
 * template omits (its list leaves it out) is not drawn, and its layout places no marker for it.
 */
export function PageTemplateView<C extends object>({
  template,
  content,
}: {
  template: PageTemplate<C>;
  content: C;
}) {
  const sections = {} as { [K in keyof C]-?: ReactNode };
  for (const key of Object.keys(template.sections) as (keyof C & string)[]) {
    const value = template.omits?.includes(key) ? undefined : content[key];
    // `display: contents`, so the marker takes no part in the layout.
    sections[key] = (
      <div data-template-section={key} className="contents">
        {value === undefined || value === null
          ? null
          : createElement(template.sections[key], value as NonNullable<C[typeof key]>)}
      </div>
    );
  }
  return <template.Layout sections={sections} />;
}
